import applyTimezone from 'helpers/applyTimezone'
import React, { useMemo } from 'react'
import { MoreVert } from '@material-ui/icons'
import { Chip, IconButton } from '@material-ui/core'
import moment from 'moment'
import {
  getFormattedDate,
  getHumanizedDuration,
  getTimeFormat,
} from 'helpers/dates'
import { STATUS_COMPLETED } from 'helpers/jobs'
import messages from 'routes/App/Apps/MyJobs/translate'
import { Flex } from 'components/common/styles'
import {
  JobItemTitleDate,
  ChipWrapper,
  IconButtonWrapper,
  DateTime,
  JobItemTitleContainer,
  JobItemTitleWrapper,
} from './JobItemTitle.styles'
import {
  CHIP_LABELS,
  DATE_FORMATS,
  DATE_FORMATS_WITH_DOT,
} from './JobItemTitle.constants'
import { JobItemTitleProps } from './JobItemTitle.types'

function JobItemTitle({
  timeTillOverdue,
  formatMessage,
  onOpenExtraMenu,
  jobItem: {
    start_date_time,
    end_date_time,
    date_completed,
    series,
    overdue,
    state,
    tasks,
    site,
  },
  usaDateFormat,
  formatPreview,
  grantedDeleteJobs,
  grantedDeleteJobSeries,
  isActive,
  usaTimeFormat,
  licenseeTimezone,
}: JobItemTitleProps) {
  const isJobCompleted = useMemo(() => state === STATUS_COMPLETED, [state])

  const isJobCanReopen = useMemo(
    () =>
      tasks.every(task => !task.optional && task.state === STATUS_COMPLETED),
    [tasks],
  )

  const repeatFrequency = useMemo(() => series && series.repeat_frequency, [
    series,
  ])

  const timeFormat = useMemo(() => getTimeFormat(usaTimeFormat), [
    usaTimeFormat,
  ])

  const dateTimeFormat = useMemo(
    () =>
      `${
        usaDateFormat
          ? DATE_FORMATS_WITH_DOT.USA
          : DATE_FORMATS_WITH_DOT.DEFAULT
      }${timeFormat}`,
    [usaDateFormat, timeFormat],
  )

  const formattedDateRange = useMemo(
    () =>
      getFormattedDate(start_date_time, formatPreview) ===
      getFormattedDate(end_date_time, formatPreview)
        ? `${getFormattedDate(
            start_date_time,
            dateTimeFormat,
          )} - ${getFormattedDate(end_date_time, timeFormat)}`
        : `${getFormattedDate(start_date_time, dateTimeFormat)}${
            end_date_time
              ? ` - ${getFormattedDate(end_date_time, dateTimeFormat)}`
              : ''
          }`,
    [start_date_time, formatPreview, end_date_time, dateTimeFormat, timeFormat],
  )

  const dateRange = useMemo(
    () =>
      formattedDateRange.length && start_date_time ? formattedDateRange : '',
    [formattedDateRange, start_date_time],
  )

  const renderLabel = (field: string, condition: boolean) => {
    const completedDate =
      date_completed &&
      getFormattedDate(
        date_completed,
        `${
          usaDateFormat ? DATE_FORMATS.USA : DATE_FORMATS.DEFAULT
        }${timeFormat}`,
      )
    const timeOverdue =
      end_date_time &&
      moment.duration(
        applyTimezone(
          isJobCompleted
            ? moment(date_completed)
            : applyTimezone(moment(), site?.timezone, licenseeTimezone),
          site?.timezone,
          licenseeTimezone,
          true,
        ).diff(
          applyTimezone(
            moment(end_date_time),
            site?.timezone,
            licenseeTimezone,
            true,
          ),
        ),
      )

    const label =
      field === CHIP_LABELS.COMPLETED
        ? `${formatMessage(messages.completed)}・${completedDate}`
        : `${formatMessage(messages.overdue)}・${getHumanizedDuration(
            timeOverdue,
            formatMessage(messages.daySymbol),
            formatMessage(messages.hourSymbol),
            formatMessage(messages.minuteSymbol),
          )}`

    if (condition) {
      return (
        <ChipWrapper
          $isCompleted={field === CHIP_LABELS.COMPLETED}
          $isActive={isActive}>
          <Chip size="small" label={label} />
        </ChipWrapper>
      )
    }
  }

  const renderLabels = () => (
    <Flex>
      {renderLabel(CHIP_LABELS.OVERDUE, overdue)}
      {renderLabel(CHIP_LABELS.COMPLETED, isJobCompleted)}
    </Flex>
  )

  const shouldDisplayDate = useMemo(() => timeTillOverdue || dateRange, [
    timeTillOverdue,
    dateRange,
  ])

  const shouldDisplayLabel = useMemo(() => isJobCompleted || overdue, [
    isJobCompleted,
    overdue,
  ])

  return (
    <JobItemTitleWrapper>
      <JobItemTitleContainer align="center">
        {shouldDisplayDate ? (
          <JobItemTitleDate justify="space-between">
            <DateTime>{timeTillOverdue || dateRange}</DateTime>
            {repeatFrequency && (
              <span>{formatMessage({ id: repeatFrequency })}</span>
            )}
          </JobItemTitleDate>
        ) : (
          shouldDisplayLabel && renderLabels()
        )}
        {(isJobCompleted ||
          grantedDeleteJobs ||
          (grantedDeleteJobSeries && !!series) ||
          isJobCanReopen) && (
          <IconButtonWrapper>
            <IconButton color="inherit" size="small" onClick={onOpenExtraMenu}>
              <MoreVert />
            </IconButton>
          </IconButtonWrapper>
        )}
      </JobItemTitleContainer>
      {shouldDisplayLabel && shouldDisplayDate && renderLabels()}
    </JobItemTitleWrapper>
  )
}

export default JobItemTitle
